import type { CityConfig } from "../city-template/types";

export const venturaConfig: CityConfig = {
  city: "Ventura",
  state: "California",
  stateAbbr: "CA",
  slug: "ventura",
  route: "/cities/ventura",
  title: "Emergency Passport Ventura, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Ventura, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Ventura County and Central Coast travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Ventura, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Ventura and Central Coast travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Ventura%20California%20coastal%20cityscape%20at%20golden%20hour%20with%20historic%20downtown%20buildings%2C%20palm-lined%20streets%20leading%20to%20the%20Pacific%20Ocean%2C%20Ventura%20Pier%20visible%20in%20the%20background%2C%20warm%20sunset%20light%20reflecting%20off%20the%20water%2C%20rolling%20hills%20and%20agricultural%20farmland%20in%20the%20distance%2C%20clean%20modern%20coastal%20town%20atmosphere%2C%20professional%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20beautiful%20coastal%20environment&width=1800&height=700&seq=ventura-hero-001&orientation=landscape",
  heroImageAlt: "Ventura California coastal cityscape at golden hour with downtown and Pacific Ocean",
  geoPosition: "34.2746;-119.2290",
  geoPlacename: "Ventura, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown LA — approximately 60–75 minutes from Ventura via US-101",
  },
  nearbyCitiesZips: [
    { name: "Ventura", zips: "93001–93004, 93009" },
    { name: "Oxnard", zips: "93030, 93033, 93035, 93036" },
    { name: "Camarillo", zips: "93010, 93012" },
    { name: "Ojai", zips: "93023, 93024" },
    { name: "Santa Paula", zips: "93060" },
    { name: "Fillmore", zips: "93015, 93016" },
    { name: "Thousand Oaks", zips: "91320, 91360, 91361, 91362" },
    { name: "Simi Valley", zips: "93063, 93065" },
    { name: "Port Hueneme", zips: "93041, 93044" },
    { name: "Moorpark", zips: "93021" },
    { name: "Santa Barbara", zips: "93101, 93103, 93105, 93109, 93110, 93111, 93117" },
    { name: "Goleta", zips: "93117, 93118, 93199" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Central Coast departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "Santa Barbara", route: "/cities/santa-barbara" },
    { name: "San Diego", route: "/cities/san-diego" },
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Pasadena", route: "/cities/pasadena" },
    { name: "Riverside", route: "/cities/riverside" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Ventura remotely via secure courier. We do not have a walk-in office in Ventura — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Central Coast travelers.",
  fpcOfficeDistance: "Serving all of Ventura County",
  serviceAreaIntro: "From downtown Ventura to the coastal communities of Oxnard and Port Hueneme, from the Ojai Valley to the Conejo Valley — Fast Passport Center serves the entire Ventura County community. We regularly help travelers from Camarillo, Thousand Oaks, Simi Valley, and Santa Barbara get their passports fast.",
  nearbyCitiesIntro: "We serve all Ventura neighborhoods and nearby Central Coast cities including Oxnard, Camarillo, Ojai, Thousand Oaks, Simi Valley, and Santa Barbara. If you are in Ventura County or nearby Santa Barbara County, we have you covered.",
  howItWorksIntro: "Most Ventura travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With Ventura County being a popular departure point for coastal cruises and Pacific flights, travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Ventura and Central Coast travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Ventura? Here are the most common ones we hear from Central Coast travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Central Coast international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Tom B.",
      location: "Ventura, CA",
      rating: 5,
      text: "My cruise to Mexico was departing in 3 days and I realized my passport was expired. Fast Passport Center got me a new one in 36 hours. Saved our family vacation completely.",
      service: "Emergency Passport",
    },
    {
      name: "Karen S.",
      location: "Oxnard, CA",
      rating: 5,
      text: "Needed to renew my passport for a business trip to London. The process was incredibly smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Mike D.",
      location: "Camarillo, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Costa Rica. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Jessica R.",
      location: "Thousand Oaks, CA",
      rating: 5,
      text: "As a frequent international traveler from Ventura County, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "ventura-1",
      name: "Ventura Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "675 E Santa Clara St",
      city: "Ventura",
      state: "CA",
      zip: "93001",
      phone: "(805) 648-4421",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Ventura",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "ventura-2",
      name: "Oxnard Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1900 N C St",
      city: "Oxnard",
      state: "CA",
      zip: "93030",
      phone: "(805) 483-1340",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Oxnard",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "ventura-3",
      name: "Camarillo Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2150 Ventura Blvd",
      city: "Camarillo",
      state: "CA",
      zip: "93010",
      phone: "(805) 482-1971",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.6 miles from Camarillo Premium Outlets",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for central Ventura County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "ventura-4",
      name: "Thousand Oaks Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "325 E Hillcrest Dr",
      city: "Thousand Oaks",
      state: "CA",
      zip: "91360",
      phone: "(805) 492-3171",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.7 miles from The Oaks mall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Conejo Valley residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "ventura-5",
      name: "Simi Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1929 Royal Ave",
      city: "Simi Valley",
      state: "CA",
      zip: "93065",
      phone: "(805) 522-4281",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Simi Valley Town Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for eastern Ventura County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "ventura-6",
      name: "Ventura County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "800 S Victoria Ave",
      city: "Ventura",
      state: "CA",
      zip: "93009",
      phone: "(805) 654-2263",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.4 miles from Ventura County Government Center",
      website: "https://www.ventura.org/clerk-recorder/",
      websiteLabel: "Ventura County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Coastal photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Ventura County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Ventura applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Ventura County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Central Coast travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Ventura." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Ventura address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Ventura before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Ventura for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Ventura County travelers." },
    { q: "How fast can I renew my passport in Ventura?", a: "Fast Passport Center offers fast passport renewal in Ventura in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Ventura in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Ventura?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Ventura residents." },
    { q: "Can I expedite a child passport in Ventura?", a: "Yes. Fast Passport Center expedites child passports in Ventura for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Ventura with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Ventura?",
      a: "With Fast Passport Center's emergency courier service, Ventura travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Ventura?",
      a: "Popular options include the Ventura Main Post Office (93001), Oxnard Post Office (93030), Camarillo Post Office (93010), and Ventura County Clerk (93009). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Ventura?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Thousand Oaks or Simi Valley?",
      a: "We serve all of Ventura County including Thousand Oaks, Simi Valley, Moorpark, Ojai, and Santa Paula. Our overnight shipping labels work from any UPS location, so your location within the county doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Ventura?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Ventura?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Ventura, Oxnard, and Camarillo offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Ventura?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Ventura?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Ventura County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Ventura", address: "2717 E Main St, Ventura, CA 93003", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Oxnard", address: "201 W Gonzales Rd, Oxnard, CA 93036", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Camarillo", address: "2275 E Ventura Blvd, Camarillo, CA 93010", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Thousand Oaks", address: "500 E Thousand Oaks Blvd, Thousand Oaks, CA 91360", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Ventura", address: "1875 E Main St, Ventura, CA 93001", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Simi Valley", address: "1925 Royal Ave, Simi Valley, CA 93065", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Ojai", address: "202 E Ojai Ave, Ojai, CA 93023", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Camarillo", address: "1950 E Ventura Blvd, Camarillo, CA 93010", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Ventura",
      address: "4260 E Main St, Ventura, CA 93003",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Oxnard",
      address: "1810 E Gonzales Rd, Oxnard, CA 93036",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Thousand Oaks",
      address: "31215 Via Colinas, Thousand Oaks, CA 91362",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Simi Valley",
      address: "2785 Cochran St, Simi Valley, CA 93065",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for eastern Ventura County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "VNT",
      name: "Ventura County Airport",
      distance: "5 miles from downtown Ventura",
      address: "555 Airport Way, Camarillo, CA 93010",
      note: "General aviation airport. Closest commercial airport is Santa Barbara or LAX.",
      flightUrl: "https://www.ventura.org/airports/",
      flightLabel: "Ventura County Airport",
      featured: true,
    },
    {
      code: "SBA",
      name: "Santa Barbara Airport",
      distance: "35 miles from Ventura",
      address: "500 James Fowler Rd, Santa Barbara, CA 93117",
      note: "Commercial airport with flights to Denver, Phoenix, San Francisco, and Seattle. Accessible via US-101.",
      flightUrl: "https://www.flysba.com/",
      flightLabel: "Santa Barbara Airport",
      featured: false,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "65 miles from Ventura",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via US-101.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
  ],
};